import React from 'react';
import {Space, Tooltip} from 'antd';
import ls from 'localstorage-slim';
import IconWrapper from '../../components/IconWrapper';
import {StyledExpensesTabIcon} from '../../components/pages/ExpensesPage/ExpensesTab/StyledExpensesTab';
import InvoicePreviewWithIcon from '../../components/pages/NetSuiteJournalEntriesPage/InvoicePreviewWithIcon';
import ExpensePolicyIcon from '../../components/pages/ExpensesPage/ExpensePolicyIcon';
import {
  expensePaymentMethodConstants,
  expensePolicyStatusConstants,
  expenseReviewStatusConstants,
  expenseTypesConstants,
  mccCategoriesConstants,
  localStorageKeysConstants,
} from '../../constants';
import {
  Invoice2Icon,
  InvoiceNoReceiptIcon,
  ProfileIcon,
  WireIcon,
  WithLessCreditCardIcon,
} from '../../icons';
import {
  AdvertisingServicesIcon,
  AirlinesIcon,
  AthleticFieldsCommercialSportsProfessionalSportsClubsSportsPromotersIcon,
  BooksPeriodicalsAndNewspapersIcon,
  BusinessServicesNotElsewhereClassifiedIcon,
  CommercialArtGraphicsPhotographyIcon,
  ComputerNetworkInformationServicesIcon,
  ComputerProgrammingDataProcessingAndIntegratedSystemDesignServicesIcon,
  ComputerSoftwareStoresIcon,
  ConsultingManagementAndPublicRelationsServicesIcon,
  CourierServiceAirAndGroundIcon,
  DepartmentStoresIcon,
  DigitalGoodsIcon,
  DirectMarketingCombinationCatalogAndRetailMerchantIcon,
  ElectronicsSalesIcon,
  EmploymentAgenciesAndTemporaryHelpServicesIcon,
  EquipmentFurnitureAndHomeFurnishingsStoresIcon,
  FinesIcon,
  FloristsIcon,
  GroceryStoresSupermarketsIcon,
  HotelsIcon,
  MiscellaneousAndSpecialtyRetailStoresIcon,
  MiscellaneousFoodStoresIcon,
  MiscellaneousGeneralMerchandiseStoresIcon,
  MiscellaneousPublishingAndPrintingIcon,
  OtherIcon,
  PassengerRailwaysIcon,
  PhotographicStudiosIcon,
  ProfessionalServicesIcon,
  RealEstateAgentsAndManagersRentalsIcon,
  RecreationServicesIcon,
  RestaurantsIcon,
  SchoolsAndEducationalServicesIcon,
  SoftwareIcon,
  SouvenirShopsIcon,
  SubscriptionMerchantsIcon,
  TaxicabsIcon,
  TaxIcon,
  TelecommunicationEquipmentIcon,
  TelecommunicationServicesIcon,
  TransportationSuburbanIcon,
  TravelAgenciesAndTourOperatorsIcon,
  VarietyStoresIcon
} from '../../icons/mccCategories';
import {alertActions} from '../../state/actions/alert.actions';
import {amountHelpers} from './amount.helpers';
import {dateHelpers} from './date.helpers';
import {objectHelpers} from './object.helpers';
import {transactionsHelpers} from './transactions.helpers';
import {vendorsHelpers} from './vendors.helpers';
import routes from '../../routes/routes.json';
import {EXPENSES_PAGE_SEARCH_KEYS} from '../../pages/ExpensesPage/expensesPage.constants';
import {reviewHelpers} from './review.helpers';

const expenseReviewStatuses = {
  [expenseReviewStatusConstants.APPROVED]: {
    backgroundColor: '#52C41A33',
    color: '#52C41A',
    key: 'expenseReviewStatuses.approved',
  },
  [expenseReviewStatusConstants.DRAFT]: {
    backgroundColor: '#C6E7F2',
    color: '#00465D',
    key: 'expenseReviewStatuses.draft',
  },
  [expenseReviewStatusConstants.IN_REVIEW]: {
    key: 'expenseReviewStatuses.in_review',
    color: '#7A2E0E',
    backgroundColor: '#FEF0C7',
  },
  [expenseReviewStatusConstants.REJECTED]: {
    backgroundColor: '#FEE4E2',
    color: '#FF4D4F',
    key: 'expenseReviewStatuses.rejected',
  },
};


export const expensesHelpers = {
  getFormattedExpenseDate: (expense, format = 'DD MMM YYYY') => {
    if (!expense || typeof expense !== 'object') return null;

    const rawExpenseDate = expense?.expense_date;

    // support range format "YYYY-MM-DD#YYYY-MM-DD"
    if (rawExpenseDate && typeof rawExpenseDate === 'string' && rawExpenseDate.includes('#')) {
      const [startRaw, endRaw] = rawExpenseDate.split('#').map(s => s.trim());
      if (!startRaw) return null;
      const startFormatted = dateHelpers.getDateFromISO(startRaw, format);
      if (endRaw) {
        const endFormatted = dateHelpers.getDateFromISO(endRaw, format);
        if (startFormatted === endFormatted) return startFormatted;

        return `${startFormatted} - ${endFormatted}`;
      }
      return startFormatted;
    }

    const start = rawExpenseDate || expense?.created_at;
    if (!start) return null;

    return dateHelpers.getDateFromISO(start, format);
  },
  getGroupedExpenses: (expenses) => {
    const daysList = {};
    const groupedExpenses = [];
    let lastAdded;

    // sort expenses by created_at
    expenses = expenses.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Calculate total price for each month
    expenses.forEach(({ created_at, price }) => {
      const date = created_at.slice(0, 7);
      daysList[date] = { price: (daysList[date]?.price || 0) + Number(price || 0) };
    });

    // Add grouping rows and transactions
    expenses.forEach(exp => {
      const date = exp.created_at.slice(0, 7);
      if (lastAdded !== date) {
        groupedExpenses.push({
          id: date,
          isMonthGrouping: true,
          created_at: date,
          ...daysList[date],
        });
        lastAdded = date;
      }
      groupedExpenses.push(exp);
    });

    return transactionsHelpers.additionalTransactionsGrouping(groupedExpenses, 'isMonthGrouping');
  },
  getReviewStatusLabel: (value, t) => {
    return reviewHelpers.getStatusLabel({value, statuses: expenseReviewStatuses, t});
  },
  getExpenseCategoryIcon: (value) => {
    return {
      [mccCategoriesConstants.ADVERTISING_SERVICES]: <AdvertisingServicesIcon />,
      [mccCategoriesConstants.AIRLINES]: <AirlinesIcon />,
      [mccCategoriesConstants.ATHLETIC_FIELDS_COMMERCIAL_SPORTS_PROFESSIONAL_SPORTS_CLUBS_SPORTS_PROMOTERS]: <AthleticFieldsCommercialSportsProfessionalSportsClubsSportsPromotersIcon />,
      [mccCategoriesConstants.BOOKS_PERIODICALS_AND_NEWSPAPERS]: <BooksPeriodicalsAndNewspapersIcon />,
      [mccCategoriesConstants.BUSINESS_SERVICES_NOT_ELSEWHERE_CLASSIFIED]: <BusinessServicesNotElsewhereClassifiedIcon />,
      [mccCategoriesConstants.COMMERCIAL_ART_GRAPHICS_PHOTOGRAPHY]: <CommercialArtGraphicsPhotographyIcon />,
      [mccCategoriesConstants.COMPUTER_NETWORK_INFORMATION_SERVICES]: <ComputerNetworkInformationServicesIcon />,
      [mccCategoriesConstants.COMPUTER_PROGRAMMING_DATA_PROCESSING_AND_INTEGRATED_SYSTEM_DESIGN_SERVICES]: <ComputerProgrammingDataProcessingAndIntegratedSystemDesignServicesIcon />,
      [mccCategoriesConstants.COMPUTER_SOFTWARE_STORES]: <ComputerSoftwareStoresIcon />,
      [mccCategoriesConstants.CONSULTING_MANAGEMENT_AND_PUBLIC_RELATIONS_SERVICES]: <ConsultingManagementAndPublicRelationsServicesIcon />,
      [mccCategoriesConstants.COURIER_SERVICES_AIR_AND_GROUND]: <CourierServiceAirAndGroundIcon />,
      [mccCategoriesConstants.DEPARTMENT_STORES]: <DepartmentStoresIcon />,
      [mccCategoriesConstants.DIGITAL_GOODS]: <DigitalGoodsIcon />,
      [mccCategoriesConstants.DIRECT_MARKETING_COMBINATION_CATALOG_AND_RETAIL_MERCHANT]: <DirectMarketingCombinationCatalogAndRetailMerchantIcon />,
      [mccCategoriesConstants.DIRECT_MARKETING_CONTINUITY_SUBSCRIPTION_MERCHANTS]: <SubscriptionMerchantsIcon />,
      [mccCategoriesConstants.ELECTRONICS_SALES]: <ElectronicsSalesIcon />,
      [mccCategoriesConstants.EMPLOYMENT_AGENCIES_AND_TEMPORARY_HELP_SERVICES]: <EmploymentAgenciesAndTemporaryHelpServicesIcon />,
      [mccCategoriesConstants.EQUIPMENT_FURNITURE_AND_HOME_FURNISHINGS_STORES]: <EquipmentFurnitureAndHomeFurnishingsStoresIcon />,
      [mccCategoriesConstants.FINES]: <FinesIcon />,
      [mccCategoriesConstants.FLORISTS]: <FloristsIcon />,
      [mccCategoriesConstants.GROCERY_STORES_SUPERMARKETS]: <GroceryStoresSupermarketsIcon />,
      [mccCategoriesConstants.HOTELS]: <HotelsIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_AND_SPECIALTY_RETAIL_STORES]: <MiscellaneousAndSpecialtyRetailStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_FOOD_STORES]: <MiscellaneousFoodStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_GENERAL_MERCHANDISE_STORES]: <MiscellaneousGeneralMerchandiseStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_PUBLISHING_AND_PRINTING]: <MiscellaneousPublishingAndPrintingIcon />,
      [mccCategoriesConstants.PASSENGER_RAILWAYS]: <PassengerRailwaysIcon />,
      [mccCategoriesConstants.PHOTOGRAPHIC_STUDIOS]: <PhotographicStudiosIcon />,
      [mccCategoriesConstants.PROFESSIONAL_SERVICES]: <ProfessionalServicesIcon />,
      [mccCategoriesConstants.REAL_ESTATE_AGENTS_AND_MANAGERS_RENTALS]: <RealEstateAgentsAndManagersRentalsIcon />,
      [mccCategoriesConstants.RECREATION_SERVICES]: <RecreationServicesIcon />,
      [mccCategoriesConstants.RESTAURANTS]: <RestaurantsIcon />,
      [mccCategoriesConstants.SCHOOLS_AND_EDUCATIONAL_SERVICES]: <SchoolsAndEducationalServicesIcon />,
      [mccCategoriesConstants.SOFTWARE]: <SoftwareIcon />,
      [mccCategoriesConstants.SOUVENIR_SHOPS]: <SouvenirShopsIcon />,
      [mccCategoriesConstants.TAXICABS]: <TaxicabsIcon />,
      [mccCategoriesConstants.TAX_PAYMENTS]: <TaxIcon />,
      [mccCategoriesConstants.TELECOMMUNICATION_EQUIPMENT]: <TelecommunicationEquipmentIcon />,
      [mccCategoriesConstants.TELECOMMUNICATION_SERVICES]: <TelecommunicationServicesIcon />,
      [mccCategoriesConstants.TRANSPORTATION_SUBURBAN]: <TransportationSuburbanIcon />,
      [mccCategoriesConstants.TRAVEL_AGENCIES_AND_TOUR_OPERATORS]: <TravelAgenciesAndTourOperatorsIcon />,
      [mccCategoriesConstants.VARIETY_STORES]: <VarietyStoresIcon />,
    }[value] ?? <OtherIcon />;
  },
  getExpenseApprovalReviews: (expense, employeeEmail) => {
    const canQuickApprove = reviewHelpers.canQuickApprove({data: expense, employeeEmail});
    return canQuickApprove ? [] : reviewHelpers.getApprovals(expense);
  },
  getExpenseDetailsUrl: (expense) => {
    if (!expense?.id) return null;

    const reviewStatusTabs = {
      [expenseReviewStatusConstants.APPROVED]: 'reviewed',
      [expenseReviewStatusConstants.REJECTED]: 'reviewed',
      [expenseReviewStatusConstants.IN_REVIEW]: 'requested',
    };

    const tab = reviewStatusTabs[expense?.review_status];
    return `${routes.expensesList}?${EXPENSES_PAGE_SEARCH_KEYS.SELECTED_EXPENSE_ID}=${expense.id}${tab ? `#${tab}` : ''}`;
  },

  getExpensePrice: (expense) => {
    if (!expense || typeof expense !== 'object') return null;

    const price = expense?.price;
    return amountHelpers.getAmountWithCurrencyCode(price > 0 ? -price : price);
  },

  getExpenseVendorNameWithLink: (expense, variant = 'text') => {
    if (!expense || typeof expense !== 'object') return null;
    return vendorsHelpers.getVendorNameWithLink({
      vendor_id: expense?.vendor_local_id,
      name: expense?.vendor_local_name || expense?.vendor,
    }, variant);
  },

  getExpenseStatusLabel: (expense, t) => {
    const reviewStatus = expense?.review_status;
    return expensesHelpers.getReviewStatusLabel(reviewStatus, t);
  },

  getExpenseTypeIcon: (expense) => {
    if (!expense) return null;
    const expenseType = expense?.expense_type;

    if ([expenseTypesConstants.INVOICE, expenseTypesConstants.RECEIPT].includes(expenseType)) return <Invoice2Icon />;

    if ([undefined, null, expenseTypesConstants.NO_RECEIPT].includes(expenseType)) return <InvoiceNoReceiptIcon />;

    return null;
  },
  getExpensePaymentMethod: (expense, t) => {
    if (!expense || typeof expense !== 'object') return null;
    const paymentMethod = expense?.payment_method;

    if (paymentMethod === expensePaymentMethodConstants.CARD) {
      const cardId = expense?.card_id;
      const userId = expense?.card?.owner?.id;

      const cardDetails = {
        card_id: cardId,
        card_name: expense?.card?.name,
        masked_pan: expense?.card?.last_4_digits,
        user_id: userId,
      }

      const value = transactionsHelpers.getTransactionCardNumberWithLink({
        transaction: cardDetails, cardSymbolsLength: 5, nameSymbolsLength: 9
      });
      return (
        <Space size='small' align='center' className='d-flex d-flex-child'>
          <IconWrapper>{<WithLessCreditCardIcon />}</IconWrapper>
          <span className={`d-inline-flex row-link-container${cardId && userId ? ' active-link' : ''} no-wrap`}>{value}</span>
        </Space>
      );
    }

    const paymentMethodDetails = {
      [expensePaymentMethodConstants.CASH]: {
        icon: <ProfileIcon />,
        name: 'cash',
      },
      [expensePaymentMethodConstants.WIRE]: {
        icon: <WireIcon />,
        name: 'wire',
      }
    }[expense?.payment_method] || undefined;

    if (!paymentMethodDetails) return null;

    return (
      <Space size='small' className='d-flex d-flex-child'>
        <IconWrapper>{paymentMethodDetails.icon}</IconWrapper>
        <span className='no-wrap'>{t(`paymentMethods.${paymentMethodDetails.name}`)}</span>
      </Space>
    );
  },
  isDefinedExpenseType: (expense) => objectHelpers.isValidValue(expense?.expense_type),
  isExpenseInReview: (expense) => expense?.review_status === expenseReviewStatusConstants.IN_REVIEW,
  isExpenseDraft: (expense) => expense?.review_status === expenseReviewStatusConstants.DRAFT,
  isExpenseInReviewOrDraft: (expense) => {
    const reviewStatus = expense?.review_status;
    return [expenseReviewStatusConstants.IN_REVIEW, expenseReviewStatusConstants.DRAFT].includes(reviewStatus);
  },
  isAvailableApproveActions: ({employeeEmail, expense, isAdmin = false}) => {
    const isInReviewStatus = expensesHelpers.isExpenseInReview(expense);
    const isInApprovalList = reviewHelpers.isEmployeeInApprovalList(expense, employeeEmail);
    const isAlreadyApproved = reviewHelpers.isAlreadyApprovedByEmployee(expense, employeeEmail);

    return isInReviewStatus && !isAlreadyApproved && (isAdmin || isInApprovalList);
  },
  isAlreadyReviewed: (expense) => {
    const reviewStatus = expense?.review_status;
    if (!reviewStatus) return false;
    return [expenseReviewStatusConstants.APPROVED, expenseReviewStatusConstants.REJECTED].includes(reviewStatus);
  },

  handleExpenseError: ({response, fallbackKey, t}) => {
    const message = response?.data?.message || response?.response?.data?.message || t(`${`expenses:validation.${fallbackKey}`}`);
    alertActions.error(message);
  },

  getExpenseRelatedObject: ({detailsWindowProps, data = [], key} = {}) => {
    if (!detailsWindowProps.open) return ;
    let obj;
    const id = detailsWindowProps?.expense?.[key];
    if (detailsWindowProps.open && id) obj = data.find(s => s.id === id);
    return obj;
  },

  hasExpenseMissingRequiredFields: (expense) => {
    if (!expense || typeof expense !== 'object') return false;

    if (!expense?.description || !objectHelpers.isValidValue(expense?.expense_type) || !(expense?.category_tags || []).length) {
      return true;
    }

    return expense?.expense_type === expenseTypesConstants.INVOICE && !expense?.company_invoice_id;
  },

  isExpenseSubmitAvailable: ({expense, isAdmin, employeeEmail}) => {
    const isReporter = expense?.reported_by?.email === employeeEmail;
    const isDraftExpense = expensesHelpers.isExpenseDraft(expense);
    return (isAdmin || isReporter) && isDraftExpense;
  },

  getExpenseReviewInfo: ({expense, translation} = {}) => {
    let icon = expensesHelpers.getExpenseTypeIcon(expense);
    const expenseType = expense?.expense_type;
    const invoiceId = expense?.company_invoice_id;
    const policyStatus = expense?.policy_status;
    const isOutOfPolicy = policyStatus === expensePolicyStatusConstants.OUT_OF_POLICY;
    const isEnableInvoicePreview = invoiceId && icon;

    if (expenseType === expenseTypesConstants.NO_RECEIPT) {
      icon = (
        <Tooltip title={translation?.('main:expenseTypes.noReceipt')}>
          <span className='d-inline-flex no-receipt'>{icon}</span>
        </Tooltip>
      );
    }

    if (expenseType === undefined || expenseType === null) {
      icon = (
        <Tooltip title={translation?.('main:missingInvoice')}>
          <span className='d-inline-flex'>{icon}</span>
        </Tooltip>
      );
    }

    return (
      <StyledExpensesTabIcon>
        <Space size='small' className='d-flex-child'>
          {
            isEnableInvoicePreview
              ? <InvoicePreviewWithIcon invoiceId={invoiceId} icon={icon} />
              : <span className='d-inline-flex'>{icon}</span>
          }
          {isOutOfPolicy && <ExpensePolicyIcon status={policyStatus} />}
        </Space>
      </StyledExpensesTabIcon>
    );
  },

  getExpenseDates: (expense) => {
    if (!expense?.expense_date) {
      if (expense?.created_at) {
        const createdAt = dateHelpers.getDateFromISO(expense.created_at, 'YYYY-MM-DD');
        return { start: createdAt, end: createdAt };
      }
      return null;
    }
    const dateParts = expense.expense_date.split('#');
    return {
      start: dateParts[0] || null,
      end: dateParts[1] || null,
    }
  },

  addExpenseSuggestion: (suggestion) => {
    if (!suggestion) return;

    const storageKey = localStorageKeysConstants.EXPENSE_SUGGESTIONS;
    const stored = ls.get(storageKey);
    const list = Array.isArray(stored) ? stored : [];

    if (list.includes(suggestion)) return;

    const next = [suggestion, ...list.filter(item => item !== suggestion)].slice(0, 5);
    ls.set(storageKey, next);
  },

  getExpenseSuggestions: () => {
    const storageKey = localStorageKeysConstants.EXPENSE_SUGGESTIONS;
    const stored = ls.get(storageKey);
    return Array.isArray(stored) ? stored : [];
  },

  removeExpenseSuggestion: (suggestion) => {
    if (!suggestion) return;

    const storageKey = localStorageKeysConstants.EXPENSE_SUGGESTIONS;
    const stored = ls.get(storageKey);
    const list = Array.isArray(stored) ? stored : [];

    const next = list.filter(item => item !== suggestion);
    ls.set(storageKey, next);
  }
}
